import Vue from 'vue'
import { MutationTree, ActionTree } from 'vuex'
import {
  Pagination,
  PaginationQueryParams,
  Repositories,
  Ticket,
  TicketOption,
  TicketPack,
  TicketPackQueryParams,
} from '~/types'
import { RootState } from '~/store'

export const state = () => ({
  filters: {
    optionIds: [] as number[],
  },
  tickets: [] as Ticket[],
  ticketStats: {} as any,
  ticketOptions: [] as TicketOption[],
  ticketPagination: {} as Pagination,
  ticketPacks: [] as TicketPack[],
  ticketPackPagination: {} as Pagination,
  ticketPackStats: {} as any,
  customerTicketPacks: [] as TicketPack[],
  customerTicketPackPagination: {} as Pagination,
  // Packs looked up individually by id (e.g. archived packs already attached to
  // an existing reservation) — customerTicketPacks only holds not-archived
  // packs, so a reservation referencing an archived pack can't resolve its name
  // from that list. Keyed by pack id to dedupe repeated lookups.
  ticketPacksById: {} as Record<number, TicketPack>,
  loadingList: {
    isTicketsLoading: false,
    isCustomerTicketPacksLoading: false,
  },
})

export type TicketState = ReturnType<typeof state>

export const mutations: MutationTree<TicketState> = {
  setFilterOptionIds(state, optionIds) {
    state.filters.optionIds = optionIds
  },
  clearFilterOptionIds(state) {
    state.filters.optionIds = []
  },
  setTickets(state, tickets: Ticket[]) {
    state.tickets = tickets
  },
  setTicketOptions(state, ticketOptions: TicketOption[]) {
    state.ticketOptions = ticketOptions
  },
  setTicketStats(state, stats: any) {
    state.ticketStats = stats
  },
  setTicketPagination(state, pagination: Pagination) {
    state.ticketPagination = pagination
  },
  setCustomerTicketPacks(state, ticketPacks: TicketPack[]) {
    state.customerTicketPacks = ticketPacks
  },
  setCustomerTicketPackPagination(state, pagination: Pagination) {
    state.customerTicketPackPagination = pagination
  },
  setTicketPackById(state, ticketPack: TicketPack) {
    Vue.set(state.ticketPacksById, ticketPack.id, ticketPack)
  },
  addNewTicket(state, ticketData: Ticket) {
    state.tickets.unshift(ticketData)
  },
  updateTicket(state, ticketData: Ticket) {
    const index = state.tickets.findIndex((item) => item.id === ticketData.id)
    if (index !== -1) state.tickets.splice(index, 1, ticketData)
  },
  updatePagination(state) {
    state.ticketPagination.total_entries += 1
    state.ticketPagination.total_pages = Math.ceil(
      state.ticketPagination.total_entries / 10
    )
  },
  setTicketPacks(state, ticketPacks: TicketPack[]) {
    state.ticketPacks = ticketPacks
  },
  setTicketPackPagination(state, pagination: Pagination) {
    state.ticketPackPagination = pagination
  },
  setTicketPackStats(state, stats: any) {
    state.ticketPackStats = stats
  },
  setLoading(state, value: { key: string; loading: boolean }) {
    state.loadingList[value.key] = value.loading
  },
}

export const actions: ActionTree<TicketState, RootState> = {
  popFilterOptionIds({ commit, state }) {
    const optionIds = state.filters.optionIds
    commit('clearFilterOptionIds')
    return optionIds
  },
  async fetchTickets(
    { commit },
    params: {
      api: Repositories
      page: number
      per: number
      branchIds: number[]
      start_date?: string
      end_date?: string
    }
  ) {
    commit('setLoading', { key: 'isTicketsLoading', loading: true })
    const { tickets, meta } = await params.api.ticketRepo.fetchTickets({
      page: params.page,
      per: params.per,
      branch_ids: params.branchIds,
      start_date: params?.start_date,
      end_date: params?.end_date,
    })
    const { pagination, stats } = meta
    commit('setTickets', tickets)
    commit('setTicketPagination', pagination)
    commit('setTicketStats', stats)
    commit('setLoading', { key: 'isTicketsLoading', loading: false })
  },
  async createTicket(
    { commit },
    params: { api: Repositories; ticketData: Ticket }
  ) {
    commit('setLoading', { key: 'isTicketsLoading', loading: true })
    const ticket = await params.api.ticketRepo.createTicket(params.ticketData)
    commit('addNewTicket', ticket)
    commit('updatePagination')
    commit('setLoading', { key: 'isTicketsLoading', loading: false })
  },
  async updateTicket(
    { commit },
    params: { api: Repositories; ticketData: Ticket }
  ) {
    commit('setLoading', { key: 'isTicketsLoading', loading: true })
    const ticket = await params.api.ticketRepo.updateTicket(params.ticketData)
    commit('updateTicket', ticket)
    commit('setLoading', { key: 'isTicketsLoading', loading: false })
  },
  async fetchTicketOptions(
    { commit },
    params: { api: Repositories; page: number; per: number }
  ) {
    const { api, ...apiParams } = params

    const { ticket_options } = await api.ticketRepo.fetchTicketOptions(
      apiParams
    )
    commit('setTicketOptions', ticket_options)
  },
  async fetchCustomerTicketPacks(
    { commit, rootState },
    params: { api: Repositories; customerId: number } & PaginationQueryParams
  ) {
    commit('setLoading', { key: 'isCustomerTicketPacksLoading', loading: true })
    const branchId = (rootState as any).auth.user.activeBranch
    const { ticket_packs, pagination } =
      await params.api.ticketRepo.fetchCustomerTicketPacks(
        branchId,
        params.customerId,
        {
          page: params.page,
          per: params.per,
        }
      )
    commit('setCustomerTicketPacks', ticket_packs)
    commit('setCustomerTicketPackPagination', pagination)
    commit('setLoading', {
      key: 'isCustomerTicketPacksLoading',
      loading: false,
    })
  },
  // Looks up a single pack by id regardless of archived status — for
  // resolving the name/details of a pack already attached to an existing
  // reservation, which customerTicketPacks (not-archived only) can't cover.
  // No-op if already cached.
  async fetchTicketPackById(
    { commit, state, rootState },
    params: { api: Repositories; customerId: number; packId: number }
  ) {
    if (state.ticketPacksById[params.packId]) return
    const branchId = (rootState as any).auth.user.activeBranch
    // The show endpoint's response is AMS-serialized with a root key
    // ({ ticket_pack: {...} }), unlike the index endpoint's collection
    // response — unwrap it here, matching fetchCustomerTicketPacks' own
    // `{ ticket_packs, pagination } = await ...` destructuring above.
    const { ticket_pack: ticketPack } =
      await params.api.ticketRepo.fetchCustomerTicketPack({
        branchId,
        customerId: params.customerId,
        id: params.packId,
      })
    if (ticketPack) commit('setTicketPackById', ticketPack)
  },
  async fetchTicketPacks(
    { commit },
    params: { api: Repositories } & TicketPackQueryParams &
      PaginationQueryParams
  ) {
    commit('setLoading', { key: 'isTicketsLoading', loading: true })
    const { api, ...apiParams } = params

    const { ticket_packs, meta } = await api.ticketRepo.fetchTicketPacks(
      apiParams
    )
    const { pagination, stats } = meta
    commit('setTicketPacks', ticket_packs)
    commit('setTicketPackPagination', pagination)
    commit('setTicketPackStats', stats)
    commit('setLoading', { key: 'isTicketsLoading', loading: false })
  },
}
